import React, { useState, useEffect, useMemo } from 'react';
import { bookingService } from '../../services/bookingService';
import { isReturnTrip } from '../../services/tripService';
import { useAuth } from '../../auth/AuthContext';
import { Booking, BookingGroup } from '../../types';
import { useRouter, Link } from '../../router/Router';
import {
  Ticket,
  Calendar,
  Clock,
  MapPin,
  Bus,
  XCircle,
  CheckCircle2,
  Trash2,
  ChevronDown,
  ChevronUp,
  Layers,
  Phone,
} from 'lucide-react';

export const MyBookings: React.FC = () => {
  const { user } = useAuth();
  const { navigate } = useRouter();

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [bookingGroups, setBookingGroups] = useState<BookingGroup[]>([]);
  const [expandedGroupIds, setExpandedGroupIds] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'UPCOMING' | 'PAST' | 'CANCELLED'>('UPCOMING');
  const [cancellingBookingId, setCancellingBookingId] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const loadData = async () => {
    setIsLoading(true);
    if (user?.uid) {
      const [bookingsList, groupsList] = await Promise.all([
        bookingService.getStudentBookings(user.uid),
        bookingService.getStudentBookingGroups(user.uid),
      ]);
      setBookings(bookingsList);
      setBookingGroups(groupsList);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [user?.uid]);

  // Real-time synchronization when bookings are modified, cancelled, or deleted by admin
  useEffect(() => {
    const handleSync = () => {
      loadData();
    };
    window.addEventListener('oto_booking_deleted', handleSync);
    window.addEventListener('storage', handleSync);
    window.addEventListener('focus', handleSync);
    return () => {
      window.removeEventListener('oto_booking_deleted', handleSync);
      window.removeEventListener('storage', handleSync);
      window.removeEventListener('focus', handleSync);
    };
  }, [user?.uid]);

  const todayStr = useMemo(() => new Date().toISOString().split('T')[0], []);

  // Filter groups
  const upcomingGroups = useMemo(() => {
    return bookingGroups.filter((g) => {
      if (
        g.status === 'CANCELLED' ||
        g.status === 'DELETED' ||
        (g as any).bookingStatus === 'CANCELLED' ||
        g.paymentStatus === 'REJECTED' ||
        g.paymentStatus === 'CANCELLED' ||
        g.paymentStatus === 'DELETED'
      )
        return false;
      const latestDate =
        g.tripDates && g.tripDates.length > 0 ? [...g.tripDates].sort().pop() || '' : '';
      return latestDate >= todayStr;
    });
  }, [bookingGroups, todayStr]);

  const pastGroups = useMemo(() => {
    return bookingGroups.filter((g) => {
      if (
        g.status === 'CANCELLED' ||
        g.status === 'DELETED' ||
        (g as any).bookingStatus === 'CANCELLED' ||
        g.paymentStatus === 'REJECTED' ||
        g.paymentStatus === 'CANCELLED' ||
        g.paymentStatus === 'DELETED'
      )
        return false;
      const latestDate =
        g.tripDates && g.tripDates.length > 0 ? [...g.tripDates].sort().pop() || '' : '';
      return latestDate < todayStr;
    });
  }, [bookingGroups, todayStr]);

  const cancelledGroups = useMemo(() => {
    return bookingGroups.filter(
      (g) =>
        g.status === 'CANCELLED' ||
        g.status === 'DELETED' ||
        (g as any).bookingStatus === 'CANCELLED' ||
        g.paymentStatus === 'REJECTED' ||
        g.paymentStatus === 'CANCELLED' ||
        g.paymentStatus === 'DELETED'
    );
  }, [bookingGroups]);

  // Standalone bookings (those not part of a known group)
  const standaloneBookings = useMemo(() => {
    const knownGroupIds = new Set(bookingGroups.map((g) => g.id));
    return bookings.filter((b) => !b.bookingGroupId || !knownGroupIds.has(b.bookingGroupId));
  }, [bookings, bookingGroups]);

  const upcomingStandalone = useMemo(() => {
    return standaloneBookings.filter(
      (b) =>
        (b.status === 'CONFIRMED' ||
          b.bookingStatus === 'CONFIRMED' ||
          b.status === 'PENDING_PAYMENT_VERIFICATION' ||
          b.bookingStatus === 'PENDING_PAYMENT_VERIFICATION') &&
        b.status !== 'CANCELLED' &&
        b.status !== 'DELETED' &&
        b.bookingStatus !== 'CANCELLED' &&
        b.paymentStatus !== 'REJECTED' &&
        b.paymentStatus !== 'CANCELLED' &&
        b.paymentStatus !== 'DELETED' &&
        b.tripDate >= todayStr
    );
  }, [standaloneBookings, todayStr]);

  const pastStandalone = useMemo(() => {
    return standaloneBookings.filter(
      (b) =>
        (b.status === 'CONFIRMED' ||
          b.bookingStatus === 'CONFIRMED' ||
          b.status === 'PENDING_PAYMENT_VERIFICATION') &&
        b.status !== 'CANCELLED' &&
        b.status !== 'DELETED' &&
        b.bookingStatus !== 'CANCELLED' &&
        b.paymentStatus !== 'REJECTED' &&
        b.paymentStatus !== 'CANCELLED' &&
        b.paymentStatus !== 'DELETED' &&
        b.tripDate < todayStr
    );
  }, [standaloneBookings, todayStr]);

  const cancelledStandalone = useMemo(() => {
    return standaloneBookings.filter(
      (b) =>
        b.status === 'CANCELLED' ||
        b.status === 'DELETED' ||
        b.bookingStatus === 'CANCELLED' ||
        b.paymentStatus === 'REJECTED' ||
        b.paymentStatus === 'CANCELLED' ||
        b.paymentStatus === 'DELETED'
    );
  }, [standaloneBookings]);

  const displayedGroups =
    activeTab === 'UPCOMING'
      ? upcomingGroups
      : activeTab === 'PAST'
      ? pastGroups
      : cancelledGroups;

  const displayedStandalone =
    activeTab === 'UPCOMING'
      ? upcomingStandalone
      : activeTab === 'PAST'
      ? pastStandalone
      : cancelledStandalone;

  const totalTabCount = displayedGroups.length + displayedStandalone.length;

  // Overall trip stats calculation
  const overallTripStats = useMemo(() => {
    const valid = bookings.filter(
      (b) =>
        b.status !== 'CANCELLED' &&
        b.status !== 'DELETED' &&
        (b.bookingStatus as string) !== 'CANCELLED' &&
        (b.bookingStatus as string) !== 'DELETED' &&
        b.paymentStatus !== 'REJECTED' &&
        b.paymentStatus !== 'CANCELLED'
    );
    const totalBooked = valid.length;
    const remaining = valid.filter((b) => b.tripDate >= todayStr && b.status !== 'COMPLETED').length;
    const completed = valid.filter((b) => b.tripDate < todayStr || b.status === 'COMPLETED').length;
    return {
      totalBooked,
      remaining,
      completed,
      isMultiTripSubscriber: totalBooked > 1,
    };
  }, [bookings, todayStr]);

  const toggleGroupExpand = (groupId: string) => {
    setExpandedGroupIds((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const handleCancelConfirm = async () => {
    if (!cancellingBookingId || !user?.uid) return;
    setIsCancelling(true);
    setActionMessage(null);

    const res = await bookingService.cancelBooking(
      { bookingId: cancellingBookingId },
      user.uid
    );

    if (res.success) {
      setActionMessage('تم إلغاء الحجز بنجاح وتحرير المقعد.');
      setCancellingBookingId(null);
      await loadData();
    } else {
      setActionMessage(res.error?.messageAr || 'تعذر إلغاء الحجز حاليًا.');
    }
    setIsCancelling(false);
  };

  return (
    <div className="booking-page-container" dir="rtl">
      {/* Header */}
      <div className="booking-page-header">
        <div className="header-breadcrumbs">
          <Link to="/student" className="breadcrumb-link">
            لوحة الطالب
          </Link>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">حجوزاتي وتذاكري</span>
        </div>
        <h1 className="page-title">سجل حجوزات رحلاتك الجامعية</h1>
        <p className="page-subtitle">
          استعرض تذاكرك الرقمية الفردية وحجوزاتك المتعددة، ومواعيد التحرك المعتمدة
        </p>
      </div>

      {/* KPI Stats Summary Card (للمشتركين في عدة رحلات وكافة الحجوزات) */}
      {overallTripStats.totalBooked > 0 && (
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            padding: '16px 20px',
            marginBottom: '20px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '14px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '10px',
                backgroundColor: '#eff6ff',
                color: '#2563eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Ticket size={20} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>
                  حالة اشتراك الرحلات الجامعية
                </h3>
                {overallTripStats.isMultiTripSubscriber && (
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 'bold',
                      backgroundColor: '#eff6ff',
                      color: '#1d4ed8',
                      padding: '2px 8px',
                      borderRadius: '999px',
                      border: '1px solid #bfdbfe',
                    }}
                  >
                    اشتراك متعدد ({overallTripStats.totalBooked} رحلات)
                  </span>
                )}
              </div>
              <span style={{ fontSize: '12px', color: '#64748b' }}>
                متابعة دقيقة لرحلاتك المحجوزة والمتبقية في جدول محاضراتك
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div
              style={{
                padding: '8px 14px',
                borderRadius: '10px',
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                textAlign: 'center',
                minWidth: '85px',
              }}
            >
              <span style={{ fontSize: '11px', color: '#64748b', display: 'block' }}>إجمالي المحجوز</span>
              <strong style={{ fontSize: '16px', fontFamily: 'monospace', color: '#0f172a' }}>
                {overallTripStats.totalBooked}
              </strong>
            </div>

            <div
              style={{
                padding: '8px 14px',
                borderRadius: '10px',
                backgroundColor: '#f0fdf4',
                border: '1px solid #bbf7d0',
                textAlign: 'center',
                minWidth: '85px',
              }}
            >
              <span style={{ fontSize: '11px', color: '#166534', display: 'block' }}>الرحلات المتبقية</span>
              <strong style={{ fontSize: '16px', fontFamily: 'monospace', color: '#16a34a' }}>
                {overallTripStats.remaining}
              </strong>
            </div>

            <div
              style={{
                padding: '8px 14px',
                borderRadius: '10px',
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                textAlign: 'center',
                minWidth: '85px',
              }}
            >
              <span style={{ fontSize: '11px', color: '#64748b', display: 'block' }}>الرحلات المكتملة</span>
              <strong style={{ fontSize: '16px', fontFamily: 'monospace', color: '#64748b' }}>
                {overallTripStats.completed}
              </strong>
            </div>
          </div>
        </div>
      )}

      {/* Tabs Row */}
      <div className="bookings-tabs-bar">
        <button
          className={`booking-tab-btn ${activeTab === 'UPCOMING' ? 'active' : ''}`}
          onClick={() => setActiveTab('UPCOMING')}
        >
          <span>الرحلات القادمة</span>
          <span className="tab-count-pill">{upcomingGroups.length + upcomingStandalone.length}</span>
        </button>

        <button
          className={`booking-tab-btn ${activeTab === 'PAST' ? 'active' : ''}`}
          onClick={() => setActiveTab('PAST')}
        >
          <span>الرحلات السابقة</span>
          <span className="tab-count-pill">{pastGroups.length + pastStandalone.length}</span>
        </button>

        <button
          className={`booking-tab-btn ${activeTab === 'CANCELLED' ? 'active' : ''}`}
          onClick={() => setActiveTab('CANCELLED')}
        >
          <span>الرحلات الملغاة والمرفوضة</span>
          <span className="tab-count-pill">{cancelledGroups.length + cancelledStandalone.length}</span>
        </button>
      </div>

      {/* Status Notice */}
      {actionMessage && (
        <div className="alert-success-banner" role="status">
          <CheckCircle2 size={18} />
          <span>{actionMessage}</span>
        </div>
      )}

      {/* Booking List */}
      {isLoading ? (
        <div className="loading-state-card">
          <div className="loading-spinner"></div>
          <p>جاري تحميل سجل الحجوزات والمجموعات...</p>
        </div>
      ) : totalTabCount === 0 ? (
        <div className="empty-state-card">
          <Ticket size={48} className="empty-state-icon" />
          <h3>
            {activeTab === 'UPCOMING'
              ? 'لا توجد لديك رحلات أو مجموعات قادمة'
              : activeTab === 'PAST'
              ? 'لا توجد رحلات سابقة مسجلة'
              : 'لا توجد رحلات ملغاة'}
          </h3>
          <p>
            {activeTab === 'UPCOMING'
              ? 'يمكنك الآن استعراض الجامعات واختيار حجز يوم واحد أو حجز عدة أيام مع رحلات العودة في الحافلات السياحية المجهزة.'
              : 'ستظهر هنا كافة الرحلات التي قمت بها مع OTO STUDENTS للطلاب.'}
          </p>
          <div className="empty-state-actions">
            <Link to="/student/universities" className="btn-primary">
              حجز رحلة جديدة الآن
            </Link>
          </div>
        </div>
      ) : (
        <div className="my-bookings-list">
          {/* 1. BOOKING GROUPS LIST */}
          {displayedGroups.map((group) => {
            const isExpanded = !!expandedGroupIds[group.id];
            const datesSorted = group.tripDates ? [...group.tripDates].sort() : [];
            const dateSpanStr = datesSorted.length > 1
              ? `من ${datesSorted[0]} ◄ إلى ${datesSorted[datesSorted.length - 1]}`
              : datesSorted[0] || 'مواعيد متعددة';

            return (
              <div key={group.id} className="booking-group-card">
                {/* Header bar */}
                <div className="booking-group-header-row">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap' }}>
                    <span className="group-code-badge">{group.groupCode}</span>
                    <span className="group-trips-count-badge">
                      <Layers size={13} style={{ display: 'inline', marginLeft: '3px' }} />
                      {group.itemCount} {group.itemCount === 1 ? 'رحلة' : group.itemCount === 2 ? 'رحلتين' : 'رحلات'}
                    </span>
                    <span className="group-mode-badge">
                      {group.bookingMode === 'ROUND_TRIP'
                        ? '🔄 ذهاب وعودة'
                        : group.bookingMode === 'MULTI_DAY'
                        ? '📅 حجز أيام متعددة'
                        : 'يوم مفرد'}
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {group.paymentStatus === 'PAID' ? (
                      <span className="pay-tag-paid">مدفوع ومعتمد ✅</span>
                    ) : group.paymentStatus === 'PENDING_VERIFICATION' ? (
                      <span className="pay-tag-pending">قيد مراجعة الدفع ⏳</span>
                    ) : group.paymentStatus === 'AWAITING_BRANCH_PAYMENT' ? (
                      <span className="pay-tag-branch">الدفع بالفرع 🏢</span>
                    ) : group.paymentStatus === 'REJECTED' ? (
                      <span className="pay-tag-danger">مرفوض - يلزم إعادة الرفع ❌</span>
                    ) : (
                      <span className="pay-tag-pending">بانتظار السداد ⏳</span>
                    )}

                    <span
                      className={`status-pill ${
                        group.status === 'CONFIRMED'
                          ? 'confirmed'
                          : group.status === 'CANCELLED'
                          ? 'cancelled'
                          : 'pending-review'
                      }`}
                    >
                      {group.status === 'CONFIRMED'
                        ? 'مؤكد'
                        : group.status === 'CANCELLED'
                        ? 'ملغى'
                        : 'قيد التنفيذ'}
                    </span>
                  </div>
                </div>

                {/* Main group info */}
                <div className="booking-group-main-body">
                  <div>
                    <h3 className="booking-group-route-title">
                      {group.bookings?.[0]?.origin || 'الإسكندرية'} ↔ {group.bookings?.[0]?.destination || group.bookings?.[0]?.universityName || 'الوجهة الجامعية'}
                    </h3>
                    <div className="booking-group-dates-span">
                      <Calendar size={15} />
                      <span>فترة السفر: <strong>{dateSpanStr}</strong></span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    <div className="price-display-box">
                      <span className="price-num">{group.totalAmount}</span>
                      <span className="price-cur">ج.م الإجمالي</span>
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {group.paymentStatus === 'REJECTED' && (
                        <>
                          <button
                            className="btn-pay-now-pill btn-retry-rejected"
                            onClick={() => navigate(`/student/payment/${group.id}`)}
                            title="إعادة المحاولة ورفع إثبات الدفع"
                          >
                            إعادة رفع الإثبات
                          </button>
                          <a
                            href="tel:01207337330"
                            className="btn-secondary"
                            style={{ padding: '0.45rem 0.85rem', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', textDecoration: 'none' }}
                            title="اتصال مباشر بخدمة العملاء"
                          >
                            <Phone size={13} />
                            <span>خدمة العملاء: 01207337330</span>
                          </a>
                          <a
                            href={`https://wa.me/201207337330?text=${encodeURIComponent(`مرحباً خدمة العملاء، لدي استفسار بخصوص الحجز رقم ${group.groupCode}`)}`}
                            target="_blank"
                            rel="noreferrer"
                            className="btn-secondary"
                            style={{ padding: '0.45rem 0.85rem', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', textDecoration: 'none', color: '#16A34A', borderColor: '#BBF7D0' }}
                            title="مراسلة خدمة العملاء عبر الواتساب"
                          >
                            <span>واتساب خدمة العملاء</span>
                          </a>
                        </>
                      )}

                      {(group.paymentStatus === 'PENDING' ||
                        group.paymentStatus === 'AWAITING_TRANSFER' ||
                        group.paymentStatus === 'AWAITING_BRANCH_PAYMENT') && (
                        <button
                          className="btn-pay-now-pill"
                          onClick={() => navigate(`/student/payment/${group.id}`)}
                        >
                          {group.paymentStatus === 'AWAITING_BRANCH_PAYMENT'
                            ? 'بيانات السداد بالفرع'
                            : 'سداد حجز المجموعة الآن'}
                        </button>
                      )}

                      <button
                        className="btn-toggle-group"
                        onClick={() => toggleGroupExpand(group.id)}
                        aria-expanded={isExpanded}
                      >
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        <span>
                          {isExpanded
                            ? 'إخفاء التذاكر'
                            : `عرض تذاكر الرحلات (${group.itemCount})`}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded child bookings accordion */}
                {isExpanded && (
                  <div className="booking-group-expanded-section">
                    <h4 className="group-items-headline">
                      <Ticket size={16} color="#0284C7" />
                      <span>تذاكر الرحلات الفردية التابعة للمجموعة:</span>
                    </h4>

                    <div className="group-trips-sublist">
                      {group.bookings && group.bookings.length > 0 ? (
                        group.bookings.map((item, idx) => (
                          <div key={item.bookingId} className="group-trip-child-card">
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                                <span style={{ fontWeight: 800, fontSize: '0.9rem', color: '#0F172A' }}>
                                  رحلة {idx + 1}:
                                </span>
                                <span className={`child-direction-pill ${isReturnTrip(item.direction) ? 'return' : 'outbound'}`}>
                                  {isReturnTrip(item.direction)
                                    ? '🔄 رحلة عودة (إلى الإسكندرية)'
                                    : '➡️ رحلة ذهاب (إلى الجامعة)'}
                                </span>
                                <span style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#64748B' }}>
                                  كود: {item.bookingCode}
                                </span>
                              </div>

                              <div className="child-trip-specs">
                                <span>
                                  <Calendar size={13} style={{ display: 'inline', marginLeft: '3px' }} />
                                  <strong>{item.date}</strong>
                                </span>
                                <span>
                                  <Clock size={13} style={{ display: 'inline', marginLeft: '3px' }} />
                                  <strong>{item.time}</strong>
                                </span>
                                <span>
                                  <Bus size={13} style={{ display: 'inline', marginLeft: '3px' }} />
                                  المقعد: <strong>رقم {item.seatNumber}</strong>
                                </span>
                                <span>
                                  <MapPin size={13} style={{ display: 'inline', marginLeft: '3px' }} />
                                  {item.origin} ➔ {item.destination}
                                </span>
                              </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <span style={{ fontWeight: 700, color: '#0F172A', fontSize: '0.95rem' }}>
                                {item.price} ج.م
                              </span>

                              <button
                                className="btn-primary"
                                style={{ padding: '0.45rem 0.85rem', fontSize: '0.8rem' }}
                                onClick={() => navigate(`/student/booking/${item.bookingId}`)}
                              >
                                <Ticket size={14} />
                                <span>عرض التذكرة</span>
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p style={{ color: '#64748B', fontSize: '0.85rem' }}>
                          جاري تحميل تفاصيل تذاكر المجموعة...
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* 2. STANDALONE BOOKINGS LIST (Single day bookings without group) */}
          {displayedStandalone.map((b) => (
            <div key={b.id} className="booking-summary-card">
              <div className="booking-card-main">
                <div className="booking-badge-header">
                  <div className="code-and-payment-group">
                    <span className="booking-ref-tag">كود: {b.bookingCode}</span>
                    {b.paymentStatus === 'PAID' ? (
                      <span className="pay-tag-paid">مدفوع ومعتمد ✅</span>
                    ) : b.paymentStatus === 'PENDING_VERIFICATION' ? (
                      <span className="pay-tag-pending">قيد مراجعة الدفع ⏳</span>
                    ) : b.paymentStatus === 'AWAITING_BRANCH_PAYMENT' ? (
                      <span className="pay-tag-branch">الدفع بالفرع 🏢</span>
                    ) : b.paymentStatus === 'REJECTED' ? (
                      <span className="pay-tag-danger">مرفوض - يلزم إعادة الرفع ❌</span>
                    ) : b.paymentStatus === 'AWAITING_TRANSFER' || b.paymentStatus === 'PENDING' ? (
                      <span className="pay-tag-pending">بانتظار السداد ⏳</span>
                    ) : (
                      <span className="pay-tag-cash">الدفع عند الركوب 💵</span>
                    )}
                  </div>
                  <span
                    className={`status-pill ${
                      b.status === 'CONFIRMED'
                        ? 'confirmed'
                        : b.status === 'PENDING_PAYMENT_VERIFICATION'
                        ? 'pending-review'
                        : 'cancelled'
                    }`}
                  >
                    {b.status === 'CONFIRMED'
                      ? 'مؤكد'
                      : b.status === 'PENDING_PAYMENT_VERIFICATION'
                      ? 'قيد التدقيق'
                      : 'ملغى'}
                  </span>
                </div>

                <h3 className="booking-uni-name">{b.universityName}</h3>

                <div className="booking-specs-grid">
                  <div className="b-spec">
                    <Calendar size={14} />
                    <span>تاريخ السفر: <strong>{b.tripDate}</strong></span>
                  </div>
                  <div className="b-spec">
                    <Clock size={14} />
                    <span>موعد التحرك: <strong>{b.departureTime}</strong></span>
                  </div>
                  <div className="b-spec">
                    <MapPin size={14} />
                    <span>المحطة: {b.origin || 'سيدي جابر / محطة مصر'}</span>
                  </div>
                  <div className="b-spec">
                    <Bus size={14} />
                    <span>المقعد المحجوز: <strong>المقعد رقم {b.seatNumber}</strong></span>
                  </div>
                </div>
              </div>

              <div className="booking-card-actions">
                <div className="price-display-box">
                  <span className="price-num">{b.price}</span>
                  <span className="price-cur">ج.م</span>
                </div>

                <div className="buttons-group">
                  {b.paymentStatus === 'REJECTED' && (
                    <>
                      <button
                        className="btn-pay-now-pill btn-retry-rejected"
                        onClick={() => navigate(`/student/payment/${b.id}`)}
                        title="إعادة المحاولة ورفع إثبات الدفع"
                      >
                        إعادة رفع الإثبات
                      </button>
                      <a
                        href="tel:01207337330"
                        className="btn-secondary"
                        style={{ padding: '0.45rem 0.85rem', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', textDecoration: 'none' }}
                        title="اتصال مباشر بخدمة العملاء"
                      >
                        <Phone size={13} />
                        <span>خدمة العملاء: 01207337330</span>
                      </a>
                      <a
                        href={`https://wa.me/201207337330?text=${encodeURIComponent(`مرحباً خدمة العملاء، لدي استفسار بخصوص الحجز رقم ${b.bookingCode}`)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-secondary"
                        style={{ padding: '0.45rem 0.85rem', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', textDecoration: 'none', color: '#16A34A', borderColor: '#BBF7D0' }}
                        title="مراسلة خدمة العملاء عبر الواتساب"
                      >
                        <span>واتساب خدمة العملاء</span>
                      </a>
                    </>
                  )}

                  {(b.paymentStatus === 'PENDING' ||
                    b.paymentStatus === 'AWAITING_TRANSFER' ||
                    b.paymentStatus === 'AWAITING_BRANCH_PAYMENT') && (
                    <button
                      className="btn-pay-now-pill"
                      onClick={() => navigate(`/student/payment/${b.id}`)}
                    >
                      {b.paymentStatus === 'AWAITING_BRANCH_PAYMENT' ? 'تفاصيل السداد بالفرع' : 'سداد الحجز الآن'}
                    </button>
                  )}

                  <button
                    className="btn-primary btn-view-ticket"
                    onClick={() => navigate(`/student/booking/${b.id}`)}
                  >
                    <Ticket size={16} />
                    <span>عرض التذكرة الرقمية</span>
                  </button>

                  {activeTab === 'UPCOMING' && (
                    <button
                      className="btn-danger-outline"
                      onClick={() => setCancellingBookingId(b.id)}
                    >
                      <Trash2 size={14} />
                      <span>إلغاء الحجز</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cancellation Confirmation Modal */}
      {cancellingBookingId && (
        <div className="modal-backdrop-overlay" dir="rtl">
          <div className="modal-card">
            <XCircle size={44} style={{ color: '#EF4444' }} />
            <h3>هل أنت متأكد من رغبتك في إلغاء هذا الحجز؟</h3>
            <p>
              سيتم تحرير مقعدك المحجوز في الحافلة فورًا وإتاحته لزملائك الطلاب.
            </p>
            <div className="modal-buttons-row">
              <button
                className="btn-danger"
                disabled={isCancelling}
                onClick={handleCancelConfirm}
              >
                {isCancelling ? 'جاري الإلغاء...' : 'تأكيد إلغاء الحجز'}
              </button>
              <button
                className="btn-secondary"
                disabled={isCancelling}
                onClick={() => setCancellingBookingId(null)}
              >
                تراجع
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
